import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Flame, 
  Trophy, 
  Heart, 
  Sparkles, 
  ShieldCheck, 
  CheckCircle,
  Check,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useCelebration } from '../contexts/CelebrationContext';
import { useLogo } from '../contexts/LogoContext';
import WinnerRevealModal from '../components/auction/WinnerRevealModal';

const AuctionPage = () => {
  const { currentUser, signInWithGoogle } = useAuth();
  const { triggerConfetti } = useCelebration();
  const { logoUrl } = useLogo();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const biddingSectionRef = useRef(null);

  // 15-Second Dramatic Reality TV Reveal State
  const [showRevealModal, setShowRevealModal] = useState(false);
  const [revealedAuctions, setRevealedAuctions] = useState({});

  // Success Modal State (matching FeedbackModal design)
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedBidDetails, setSubmittedBidDetails] = useState(null);

  // Bidding Form State
  const [bidAmount, setBidAmount] = useState('');
  const [devoteeName, setDevoteeName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Auto-fill logged-in devotee's contact details (matching FindMyPhotos / Portal)
  useEffect(() => {
    if (currentUser) {
      const name = currentUser.displayName || 
        `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || 
        currentUser.name || '';
      if (name && !devoteeName) {
        setDevoteeName(name);
      }
      if (currentUser.email && !email) {
        setEmail(currentUser.email);
      }
      const ph = currentUser.phone || currentUser.phoneNumber || '';
      if (ph && !phone) {
        setPhone(ph);
      }
    }
  }, [currentUser]);

  useEffect(() => {
    fetchAuctions();
  }, []);

  const fetchAuctions = async () => {
    try {
      setLoading(true);
      const res = await api.get('/auctions');
      if (res.ok) {
        const json = await res.json();
        const items = json.data || [];
        setAuctions(items);
        if (items.length > 0 && !selectedAuction) {
          setSelectedAuction(items[0]);
          initializeBidAmount(items[0]);
        } else if (selectedAuction) {
          const updated = items.find(i => i.id === selectedAuction.id);
          if (updated) setSelectedAuction(updated);
        }
      }
    } catch (err) {
      console.error('Error fetching auctions:', err);
    } finally {
      setLoading(false);
    }
  };

  const initializeBidAmount = (item) => {
    if (!item) return;
    const hasBids = item.bids && item.bids.length > 0;
    const current = Number(item.currentHighestBid) || Number(item.startingBid) || 0;
    const nextMin = hasBids ? (current + 1) : Number(item.startingBid);
    setBidAmount(String(nextMin));
  };

  const handleSelectAuction = (item) => {
    setSelectedAuction(item);
    initializeBidAmount(item);

    // Smooth scroll down to the bidding form / submit section
    setTimeout(() => {
      if (biddingSectionRef.current) {
        biddingSectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 50);
  };

  // Quick increment calculation
  const currentHighest = selectedAuction ? (Number(selectedAuction.currentHighestBid) || Number(selectedAuction.startingBid) || 0) : 0;
  const hasExistingBids = selectedAuction && selectedAuction.bids && selectedAuction.bids.length > 0;
  const minRequiredBid = hasExistingBids ? (currentHighest + 1) : (selectedAuction ? Number(selectedAuction.startingBid) : 0);

  const handleQuickAdd = (amountToAdd) => {
    const base = Number(bidAmount) || minRequiredBid;
    setBidAmount(String(base + amountToAdd));
  };

  const handlePlaceBid = async (e) => {
    e.preventDefault();
    if (!selectedAuction) return;

    const numAmount = Number(bidAmount);
    if (isNaN(numAmount) || numAmount < minRequiredBid) {
      toast.error(`Your bid must be higher than the current leading bid ($${currentHighest}).`, {
        duration: 5000,
        icon: '⚠️'
      });
      return;
    }

    const nameToSubmit = devoteeName.trim() || currentUser?.displayName || 'Devotee Family';
    const contactPhone = phone.trim() || currentUser?.phone || currentUser?.phoneNumber || '';
    const contactEmail = email.trim() || currentUser?.email || '';

    try {
      setSubmitting(true);
      const res = await api.post(`/auctions/${selectedAuction.id}/bid`, {
        userId: currentUser?.uid || currentUser?.id || null,
        devoteeName: nameToSubmit,
        phone: contactPhone,
        email: contactEmail,
        amount: numAmount,
        prayerNote: '',
        isAnonymous: false
      });

      if (res.ok) {
        const json = await res.json();
        const updated = json.data;

        // Trigger celebratory confetti
        try {
          if (triggerConfetti) triggerConfetti();
        } catch (e) {
          console.log('Confetti trigger error:', e);
        }

        // Update local state
        setSelectedAuction(updated);
        setAuctions(prev => prev.map(a => a.id === updated.id ? updated : a));
        initializeBidAmount(updated);

        // Open Success Popup Modal (matching FeedbackModal style with BOW logo)
        setSubmittedBidDetails({
          amount: numAmount,
          offeringTitle: selectedAuction.title,
          category: selectedAuction.category,
          devoteeName: nameToSubmit
        });
        setShowSuccessModal(true);
      } else {
        const errJson = await res.json();
        toast.error(errJson.error || 'Failed to place bid');
      }
    } catch (err) {
      console.error('Bid error:', err);
      toast.error('Connection error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50/50 via-white to-amber-50/30 py-8 sm:py-12 text-gray-800">
      <Helmet>
        <title>🌺 Bid For Divine Blessings | Maa Durga Saree & Sacred Laddu Auction</title>
        <meta 
          name="description" 
          content="Participate in the sacred auction for Maa Durga's Sanctified Saree and Maha Laddu Prasad. Bid with devotion, receive divine blessings, and support our cultural traditions." 
        />
      </Helmet>

      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Logo Header (hidden on mobile, visible on sm and up) */}
        <div className="hidden sm:flex justify-center mb-4">
          <Link 
            to="/"
            className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-orange-500 via-amber-600 to-yellow-600 rounded-full flex items-center justify-center overflow-hidden shadow-xl hover:scale-105 transition-transform duration-300 ring-4 ring-orange-200/60"
          >
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="w-full h-full object-cover" />
            ) : (
              <span className="text-white font-extrabold text-2xl sm:text-3xl">B</span>
            )}
          </Link>
        </div>

        {/* Clean Header Section (matching FindMyPhotosPage) */}
        <div className="text-center max-w-3xl mx-auto mb-8">
          <div className="inline-flex items-center px-3.5 py-1 rounded-full bg-orange-100 text-orange-800 border border-orange-200 text-xs font-bold uppercase tracking-wider mb-3 shadow-sm">
            <span className="mr-1.5">🌺🙏</span>
            BID FOR DIVINE BLESSINGS
            <span className="ml-1.5">🙏🌺</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
            Maa Durga's Saree &{' '}
            <span className="bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 bg-clip-text text-transparent">
              Sacred Laddu Auction
            </span>
          </h1>

          <p className="mt-3 text-sm sm:text-base text-gray-600 font-medium">
            Bid with devotion • Receive blessings • Support our cultural traditions
          </p>


        </div>

        {/* MAIN AUCTION CONTENT */}
        {loading ? (
          <div className="py-24 text-center">
            <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-amber-900 font-semibold text-lg">Loading sacred offerings...</p>
          </div>
        ) : auctions.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center shadow-xl border border-amber-100">
            <Flame className="w-16 h-16 text-amber-600 mx-auto mb-4 animate-bounce" />
            <h2 className="text-2xl font-bold text-gray-900">Upcoming Sacred Auction</h2>
            <p className="text-gray-600 mt-2 max-w-md mx-auto">
              The sacred auction for Maa Durga's Saree and Sacred Laddu is being prepared. Please check back shortly to place your devotional bids!
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* STEP 1: PROMINENT OFFERING SELECTION CARDS (Option 1) */}
            <div>
              <div className="text-center mb-4">
                <span className="text-xs font-bold text-amber-900 uppercase tracking-wider bg-amber-100/90 px-4 py-1.5 rounded-full border border-amber-300 shadow-xs">
                  ✨ Select Sacred Offering to Bid On ✨
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {auctions.map((item) => {
                  const isSelected = selectedAuction?.id === item.id;
                  const itemImage = (item.images && item.images[0]) || (item.category?.includes('Saree') ? '/maa_durga_no_third_eye.jpg' : '/sacred_maha_laddu_prasad.jpg');
                  const itemIcon = item.category?.includes('Saree') ? '🌺' : '🪔';

                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleSelectAuction(item)}
                      className={`relative w-full text-left p-4 sm:p-5 rounded-3xl transition-all duration-300 flex items-center gap-4 sm:gap-5 cursor-pointer transform ${
                        isSelected
                          ? 'bg-gradient-to-br from-amber-50 via-orange-50/70 to-yellow-50 border-2 border-orange-500 shadow-xl ring-4 ring-orange-500/25 scale-[1.02]'
                          : 'bg-white border-2 border-gray-200/80 hover:border-amber-400 hover:shadow-lg opacity-85 hover:opacity-100 hover:scale-[1.01]'
                      }`}
                    >
                      {/* Offering Image */}
                      <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden shadow-md flex-shrink-0 border-2 border-amber-200 bg-amber-950">
                        <img
                          src={itemImage}
                          alt={item.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = item.category?.includes('Saree') 
                              ? '/maa_durga_no_third_eye.jpg' 
                              : '/sacred_maha_laddu_prasad.jpg';
                          }}
                        />
                        <div className="absolute top-1.5 left-1.5 w-6 h-6 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-xs">
                          {itemIcon}
                        </div>
                      </div>

                      {/* Offering Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${
                            isSelected
                              ? 'bg-orange-600 text-white shadow-xs'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {item.category}
                          </span>

                          {/* Leading Bid Badge */}
                          <div className="text-right">
                            <span className="text-[10px] text-gray-500 block leading-tight font-medium">
                              {item.status === 'completed' && !revealedAuctions[item.id] ? 'Auspicious Winner' : 'Leading Bid'}
                            </span>
                            {item.status === 'completed' && !revealedAuctions[item.id] ? (
                              <span className="text-xs sm:text-sm font-extrabold text-amber-800 bg-amber-100/90 px-2.5 py-0.5 rounded-full inline-flex items-center gap-1 border border-amber-300 shadow-xs">
                                🔒 Sealed Bid
                              </span>
                            ) : (
                              <span className="text-lg sm:text-xl font-black text-orange-600 leading-tight">
                                ${item.currentHighestBid}
                              </span>
                            )}
                          </div>
                        </div>

                        <h3 className="font-extrabold text-gray-900 text-base sm:text-lg line-clamp-1">
                          {item.title}
                        </h3>

                        {/* Status / Selection Indicator */}
                        <div className="mt-2.5 flex items-center gap-2">
                          {isSelected ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-black text-emerald-800 bg-emerald-100/90 px-3 py-1 rounded-full border border-emerald-300 shadow-xs">
                              <CheckCircle className="w-4 h-4 text-emerald-600" /> Currently Selected to Bid
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-orange-100 hover:text-orange-800 px-3 py-1 rounded-full border border-gray-200 transition-colors">
                              Tap to Select & Bid →
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* DETAILED BIDDING PANEL FOR THE SELECTED OFFERING */}
            {selectedAuction && (
              <div 
                ref={biddingSectionRef}
                id="bidding-form-section"
                className="bg-white rounded-3xl shadow-2xl border border-amber-200/80 overflow-hidden transition-all scroll-mt-20 sm:scroll-mt-24"
              >
                {/* MAIN CONTENT AREA: BIDDING FORM & OFFERING DETAILS */}
                <div className="p-6 sm:p-10 space-y-10">
                    {selectedAuction.status === 'completed' ? (
                      <div className="bg-gradient-to-br from-amber-50 via-yellow-50/70 to-orange-50 p-8 sm:p-10 rounded-3xl border-2 border-amber-300 text-center shadow-lg">
                        <Trophy className="w-16 h-16 text-yellow-600 mx-auto mb-3 animate-bounce" />
                        <span className="text-xs uppercase tracking-widest font-extrabold text-amber-800 bg-amber-200/60 px-3 py-1 rounded-full border border-amber-300">
                          {revealedAuctions[selectedAuction.id] ? '✨ Winner Officially Revealed ✨' : '🔒 Auspicious Sealed Mystery Bids'}
                        </span>
                        <h3 className="text-2xl sm:text-3xl font-black text-gray-900 mt-3">
                          Maa Durga's Sacred Auction Has Concluded!
                        </h3>
                        <p className="text-gray-600 mt-2 max-w-lg mx-auto text-sm sm:text-base leading-relaxed">
                          {revealedAuctions[selectedAuction.id] 
                            ? "Maa Durga's divine offerings have been officially awarded to the blessed devotee family:" 
                            : "The final devotional bids have been sanctified and sealed under divine auspices. Launch the official 15-second reality reveal ceremony to unveil the winner!"}
                        </p>

                        {/* Unveiled Winner Details Card (Shows after 15s reveal) */}
                        {revealedAuctions[selectedAuction.id] && (
                          <div className="mt-6 inline-block bg-white px-8 py-4 rounded-3xl shadow-xl border-2 border-amber-200 animate-in zoom-in-95 duration-300">
                            <span className="text-xs uppercase tracking-wider text-amber-800 font-bold block mb-1">
                              👑 Auspicious Winning Devotee Family
                            </span>
                            <span className="text-2xl sm:text-3xl font-black text-orange-600 block">
                              {selectedAuction.winner?.devoteeName || selectedAuction.currentHighestBidder}
                            </span>
                            <span className="text-sm font-bold text-gray-700 block mt-1">
                              Winning Holy Offering: <strong className="text-emerald-700 text-lg sm:text-xl">${selectedAuction.winner?.amount || selectedAuction.currentHighestBid}</strong>
                            </span>
                          </div>
                        )}

                        {/* 15-Second Grand Winner Reveal Button */}
                        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                          <button
                            type="button"
                            onClick={() => setShowRevealModal(true)}
                            className="bg-gradient-to-r from-red-700 via-orange-600 to-amber-600 hover:from-red-800 hover:to-orange-700 text-white font-extrabold text-base sm:text-lg px-8 py-4 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform active:scale-95 flex items-center justify-center gap-3 border border-amber-300/40"
                          >
                            <Sparkles className="w-5 h-5 text-yellow-300" />
                            {revealedAuctions[selectedAuction.id] 
                              ? 'Replay 15-Second Grand Reveal 🎬' 
                              : '🎬 Launch 15-Second Grand Winner Reveal 🏆'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="max-w-2xl mx-auto">
                        <form onSubmit={handlePlaceBid} className="space-y-5">
                            {/* Bid Amount Input + Quick Increments */}
                            <div>
                              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                                Your Devotional Bid Amount ($ USD) *
                              </label>
                              <div className="relative">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-gray-400">
                                  $
                                </span>
                                <input
                                  type="number"
                                  required
                                  min={minRequiredBid}
                                  step="1"
                                  value={bidAmount}
                                  onChange={(e) => setBidAmount(e.target.value)}
                                  className="w-full pl-10 pr-4 py-3.5 text-2xl font-black text-orange-600 border-2 border-amber-300 rounded-2xl focus:border-orange-500 focus:ring-4 focus:ring-orange-500/20 shadow-sm"
                                  placeholder={String(minRequiredBid)}
                                />
                              </div>

                              {/* Quick increment buttons */}
                              <div className="flex flex-wrap items-center gap-2 mt-3">
                                <span className="text-xs text-gray-500 font-semibold">Quick Add:</span>
                                {[25, 50, 100, 250].map((inc) => (
                                  <button
                                    key={inc}
                                    type="button"
                                    onClick={() => handleQuickAdd(inc)}
                                    className="bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 px-3 py-1 rounded-xl text-xs font-bold transition-all transform active:scale-95"
                                  >
                                    +${inc}
                                  </button>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => setBidAmount(String(minRequiredBid))}
                                  className="text-xs text-gray-500 hover:text-gray-700 underline ml-auto"
                                >
                                  Reset (${minRequiredBid})
                                </button>
                              </div>
                            </div>

                            {/* Auth Sign-in Prompt for guests */}
                            {!currentUser && (
                              <div className="bg-gradient-to-r from-orange-50 via-amber-50 to-yellow-50 p-3.5 rounded-2xl border border-amber-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-sm">
                                <div className="flex items-center gap-2.5">
                                  <span className="text-xl flex-shrink-0">🙏</span>
                                  <div>
                                    <p className="text-xs font-bold text-gray-800">Have a Google or BOW account?</p>
                                    <p className="text-[11px] text-gray-500">Sign in to auto-fill your name, email, and phone number instantly.</p>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      if (signInWithGoogle) await signInWithGoogle();
                                    } catch (e) {
                                      console.error('Sign-in error:', e);
                                    }
                                  }}
                                  className="bg-white hover:bg-gray-50 text-gray-800 border border-gray-300 px-3.5 py-1.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 shadow-sm transition-all hover:border-gray-400 whitespace-nowrap self-start sm:self-auto"
                                >
                                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                                  </svg>
                                  Sign in to Auto-fill
                                </button>
                              </div>
                            )}

                            {/* Devotee / Family Name input (Editable) */}
                            <div>
                              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                                Devotee / Family Name *
                              </label>
                              <input
                                type="text"
                                required
                                placeholder="e.g. Sharma Family or Ananya Patel"
                                value={devoteeName}
                                onChange={(e) => setDevoteeName(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base font-semibold focus:ring-2 focus:ring-orange-500 shadow-sm"
                              />
                            </div>

                            {/* Submit Button */}
                            <button
                              type="submit"
                              disabled={submitting}
                              className="w-full bg-gradient-to-r from-red-700 via-orange-600 to-amber-600 hover:from-red-800 hover:to-orange-700 text-white font-extrabold text-base sm:text-lg py-4 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2 border border-amber-300/40"
                            >
                              <Flame className="w-5 h-5 text-yellow-300 animate-pulse" />
                              {submitting ? 'Placing Sacred Bid...' : `Submit Devotional Bid of $${bidAmount || minRequiredBid}`}
                            </button>

                            {/* Demo / Testing Preview Button for the 15-Second Dramatic Reveal */}
                            <div className="text-center pt-2">
                              <button
                                type="button"
                                onClick={() => setShowRevealModal(true)}
                                className="text-xs text-amber-900/80 hover:text-amber-950 font-semibold underline inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                              >
                                <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                                Preview 15-Second Reality TV Reveal (Demo)
                              </button>
                            </div>
                          </form>
                      </div>
                    )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Trust Badges at Bottom */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3 sm:gap-5 text-xs font-bold text-gray-700">
          <span className="bg-white border border-amber-200/80 px-4 py-2 rounded-2xl shadow-sm flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-600" /> Sanctified Offerings
          </span>
          <span className="bg-white border border-red-200/80 px-4 py-2 rounded-2xl shadow-sm flex items-center gap-2">
            <Heart className="w-4 h-4 text-red-600" /> Vedic Blessing Ceremony
          </span>
          <span className="bg-white border border-emerald-200/80 px-4 py-2 rounded-2xl shadow-sm flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600" /> 100% Cultural Support
          </span>
        </div>
      </div>

      {/* BID SUCCESS POPUP MODAL (matching FeedbackModal style with BOW logo) */}
      {showSuccessModal && submittedBidDetails && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden border border-amber-200/80 animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="relative bg-gradient-to-r from-red-950 via-red-900 to-amber-950 text-white p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 p-1 border border-white/20 backdrop-blur-sm overflow-hidden flex-shrink-0">
                    <img
                      src={logoUrl || '/logo512.png'}
                      alt="Beats of Washington Logo"
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.src = '/logo512.png';
                      }}
                    />
                  </div>
                  <div>
                    <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-amber-300 flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-amber-300" /> Sacred Devotional Auction
                    </span>
                    <h2 className="text-base sm:text-lg font-extrabold leading-tight">
                      Jai Mata Di!
                    </h2>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSuccessModal(false)}
                  className="text-white/70 hover:text-white p-1.5 rounded-full hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-5 sm:p-6 text-center space-y-4">
              {/* Circular BOW logo with ambient glow and verified checkmark */}
              <div className="relative w-20 h-20 sm:w-24 sm:h-24 mx-auto">
                <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-orange-400 to-amber-500 blur-md opacity-40 animate-pulse"></div>
                
                <div className="relative w-full h-full rounded-full bg-white p-2 shadow-xl border-2 border-orange-200 flex items-center justify-center overflow-hidden animate-bounce" style={{ animationDuration: '2.5s' }}>
                  <img
                    src={logoUrl || '/logo512.png'}
                    alt="Beats of Washington Logo"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.src = '/logo512.png';
                    }}
                  />
                </div>

                <div className="absolute -bottom-1 -right-1 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-lg border-2 border-white">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 stroke-[3]" />
                </div>
              </div>

              <div>
                <h3 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">
                  Thank You for Your Devotion!
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1.5 max-w-md mx-auto leading-relaxed">
                  Your devotional bid of <strong className="text-orange-600 font-extrabold">${submittedBidDetails.amount}</strong> for{' '}
                  <strong className="text-gray-900">{submittedBidDetails.offeringTitle}</strong> has been placed.
                </p>
              </div>

              {/* Devotee Info Summary Card */}
              <div className="bg-gradient-to-br from-amber-50/80 to-orange-50/60 rounded-2xl p-3.5 border border-amber-200/80 text-center text-xs">
                <span className="text-gray-500 font-medium mr-1.5">Devotee / Family:</span>
                <span className="font-bold text-gray-900">{submittedBidDetails.devoteeName}</span>
              </div>

              <p className="text-[11px] text-gray-500 italic">
                🌸 May Maa Durga shower divine blessings, wellness, and prosperity upon you and your family!
              </p>

              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => setShowSuccessModal(false)}
                  className="px-8 py-2.5 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-sm shadow-md hover:from-orange-600 hover:to-amber-600 transition-all duration-200 hover:scale-105"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 15-SECOND DRAMATIC REALITY TV WINNER REVEAL MODAL */}
      <WinnerRevealModal
        isOpen={showRevealModal}
        onClose={() => setShowRevealModal(false)}
        auction={selectedAuction}
        onRevealComplete={() => {
          if (selectedAuction) {
            setRevealedAuctions(prev => ({
              ...prev,
              [selectedAuction.id]: true
            }));
          }
        }}
      />
    </div>
  );
};

export default AuctionPage;
